import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  CreateReleaseData,
  InstallerRelease,
  InstallerReleaseList,
  InstallerStats,
  PaginationParams,
  UpdateReleaseData,
} from "../types";

const installerReleaseSchema = z.object({
  id: z.string(),
  version: z.string(),
  status: z.enum(["draft", "published", "archived"]),
  downloadUrl: z.string(),
  fileSize: z.coerce.number(),
  fileName: z.string(),
  updatePackageUrl: z.string().nullable(),
  updatePackageSize: z.coerce.number().nullable(),
  updatePackageName: z.string().nullable(),
  releaseNotes: z.string(),
  publishedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  downloads: z.number(),
  platform: z.enum(["windows", "macos", "linux"]),
  environment: z.enum(["dev", "staging", "production"]),
});

const installerReleaseListSchema = z.object({
  data: z.array(installerReleaseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

const installerStatsSchema = z.object({
  totalReleases: z.number(),
  publishedReleases: z.number(),
  draftReleases: z.number(),
  totalDownloads: z.number(),
  latestVersion: z.string(),
});

export const getReleases = async (params?: PaginationParams): Promise<InstallerReleaseList> => {
  const response = await apiClient.get("/admin/installer/releases", { params });
  const parsed = installerReleaseListSchema.parse(response.data);
  return parsed;
};

export const getRelease = async (id: string): Promise<InstallerRelease> => {
  const response = await apiClient.get(`/admin/installer/releases/${id}`);
  const parsed = installerReleaseSchema.parse(response.data);
  return parsed;
};

export const createRelease = async (
  data: CreateReleaseData,
  onUploadProgress?: (progress: number) => void
): Promise<InstallerRelease> => {
  const formData = new FormData();
  formData.append("version", data.version);
  formData.append("releaseNotes", data.releaseNotes);
  formData.append("platform", data.platform);
  formData.append("environment", data.environment);
  formData.append("installerFile", data.installerFile);
  formData.append("updatePackage", data.updatePackage);

  const response = await apiClient.post("/admin/installer/releases", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (event) => {
      if (event.total && onUploadProgress) {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        onUploadProgress(percentCompleted);
      }
    },
  });
  const parsed = installerReleaseSchema.parse(response.data);
  return parsed;
};

export const updateRelease = async (
  id: string,
  data: UpdateReleaseData
): Promise<InstallerRelease> => {
  const response = await apiClient.patch(`/admin/installer/releases/${id}`, data);
  const parsed = installerReleaseSchema.parse(response.data);
  return parsed;
};

export const deleteRelease = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/installer/releases/${id}`);
};

export const publishRelease = async (id: string): Promise<InstallerRelease> => {
  const response = await apiClient.post(`/admin/installer/releases/${id}/publish`);
  const parsed = installerReleaseSchema.parse(response.data);
  return parsed;
};

export const archiveRelease = async (id: string): Promise<InstallerRelease> => {
  const response = await apiClient.post(`/admin/installer/releases/${id}/archive`);
  const parsed = installerReleaseSchema.parse(response.data);
  return parsed;
};

export const getStats = async (): Promise<InstallerStats> => {
  const response = await apiClient.get("/admin/installer/releases/stats");
  const parsed = installerStatsSchema.parse(response.data);
  return parsed;
};

export const installerApi = {
  getReleases,
  getRelease,
  createRelease,
  updateRelease,
  deleteRelease,
  publishRelease,
  archiveRelease,
  getStats,
};
